import { format, formatISO } from 'date-fns'
import { FeesTypeItem, RegistrationFeesType } from '../models/event-fees/event-fees.interface'
import { TCustomSchedule } from '../models/event-scheduling/event-scheduling.interface'
import { IDateInterface } from '../models/interface.helper'

export const convertTime = (classStart: string, increment: string) => {
  const [classBaseHours, classBaseMinutes, classBasePeriod] = classStart.split(/[:\s]/)

  const [incrementHours, incrementMinutes, incrementSeconds] = increment.split(':').map(Number)

  const startTotalSeconds = Number(classBaseHours) * 3600 + Number(classBaseMinutes) * 60

  const totalIncrementSeconds =
    Number(incrementHours) * 3600 + Number(incrementMinutes) * 60 + incrementSeconds

  // Add the increment seconds
  const totalSeconds = startTotalSeconds + totalIncrementSeconds

  let hours = Math.floor(totalSeconds / 3600)
  let minutes = Math.floor((totalSeconds % 3600) / 60)
  let seconds = totalSeconds % 60
  // Determine AM/PM
  if (classBasePeriod === 'AM') {
    hours = hours % 12
  } else {
    hours = hours % 12

    hours = hours ? hours : 12 // If hour is 0, set it to 12
  }

  // Format the result
  const formattedHours = hours < 10 ? '0' + hours : hours
  const formattedMinutes = minutes < 10 ? '0' + minutes : minutes
  const formattedSeconds = seconds < 10 ? '0' + seconds : seconds
  return `${formattedHours === '00' ? '12' : formattedHours}:${formattedMinutes}:${formattedSeconds} ${classBasePeriod}`
}

export const formatNotificationTimestamp = (utcTimestamp: string | Date | null) => {
  if (!utcTimestamp) return 'Last sent: not yet sent'

  try {
    const date = typeof utcTimestamp === 'string' ? Date.parse(utcTimestamp) : utcTimestamp
    return `Last sent ${format(date, 'h:mm a')} ${format(date, 'MMM dd')}`
  } catch (error) {
    console.error('Error formatting timestamp:', error)
    return 'Unknown'
  }
}

// helper function to check the increment string is in the correct format of "00:00:00"
export const isValidIncrementFormat = (incrementString: string) => {
  return /^\d{2}:\d{2}:\d{2}$/.test(incrementString)
}

export const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

/**
 * Converts various date formats (including Firestore Timestamps) to JavaScript Date
 * @param dateInput - Can be a Firestore Timestamp, Date object, date string, or null/undefined
 * @returns JavaScript Date object or null if conversion fails
 */
export const convertToDate = (dateInput: any): Date | null => {
  if (!dateInput) return null
  try {
    // Handle Firestore Timestamp objects
    if (
      typeof dateInput === 'object' &&
      dateInput !== null &&
      'seconds' in dateInput &&
      typeof dateInput.seconds === 'number'
    ) {
      return new Date(dateInput.seconds * 1000)
    }
    // Handle Firestore Timestamp with toDate() method
    if (typeof dateInput.toDate === 'function') {
      return dateInput.toDate()
    }
    // Handle regular Date objects
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput
    }
    // Handle date strings and numbers
    const date = new Date(dateInput)
    return isNaN(date.getTime()) ? null : date
  } catch (error) {
    console.warn('Failed to convert date:', dateInput, error)
    return null
  }
}

/**
 * Converts various date formats to underscore-separated format (M_D_YYYY)
 * @param dateInput - Can be a Firestore Timestamp, Date object, date string, or null/undefined
 * @param fallback - Fallback value if date conversion fails
 * @returns Date string in M_D_YYYY format (e.g., "3_1_2025")
 */
export const formatDateWithUnderscores = (
  dateInput: any,
  fallback: string = 'unknown_date'
): string => {
  const date = convertToDate(dateInput)
  if (!date) return fallback

  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear()

  return `${month}_${day}_${year}`
}

export const extractDatePart = (dateInput?: Date | string | IDateInterface): string => {
  let formatedDate: number

  if ((dateInput as IDateInterface)?.seconds) {
    formatedDate = new Date((dateInput as IDateInterface).seconds * 1000).getTime()
  } else {
    formatedDate = new Date(dateInput as Date | string).getTime()
  }

  return formatISO(formatedDate, { representation: 'date' }) // Returns 'yyyy-MM-dd'
}

const getSchedulingFullDate = (item: TCustomSchedule | RegistrationFeesType | FeesTypeItem) => {
  const date = new Date(item.startDate)
  const hours =
    item.startTimeFormat === 'PM'
      ? (Number(item.startTimeHours) % 12) + 12
      : Number(item.startTimeHours) % 12

  date.setHours(hours, Number(item.startTimeMinutes), 0, 0)
  return date
}

export const sortScheduling = (
  scheduling: (TCustomSchedule | RegistrationFeesType | FeesTypeItem)[]
) => {
  return scheduling.sort((a, b): number => {
    const hasTimeA = a.startDate && (a.startTimeHours || a.startTimeMinutes)
    const hasTimeB = b.startDate && (b.startTimeHours || b.startTimeMinutes)

    // If both have times -> sort by date & time
    if (hasTimeA && hasTimeB) {
      // First, sort by startDate
      const dateA = getSchedulingFullDate(a).getTime()
      const dateB = getSchedulingFullDate(b).getTime()
      return dateA - dateB
    }

    // If one of them doesn't have time -> sort by order
    const orderA = (a as RegistrationFeesType).order || 0
    const orderB = (b as RegistrationFeesType).order || 0

    if (orderA === 0 && orderB === 0) return 0
    if (orderB === 0) return 1
    if (orderB === 0) return -1

    return orderA - orderB
  })
}

export function daysBetweenDates(date1: string | Date, date2: string | Date): number {
  // Convert date strings to Date objects if needed
  const firstDate = new Date(date1)
  const secondDate = new Date(date2)

  // Calculate the difference in milliseconds
  const differenceInMs = Math.abs(secondDate.getTime() - firstDate.getTime())

  // Convert milliseconds to days
  const millisecondsPerDay = 24 * 60 * 60 * 1000

  return Math.ceil(differenceInMs / millisecondsPerDay)
}

export const getFullDate = (
  date: string,
  time?: { format: string; hour: string; minutes: string }
) => {
  const fullDate = new Date(date)

  if (time) {
    let hours = parseInt(time.hour, 10)
    const minutes = parseInt(time.minutes, 10)

    if (time.format === 'PM' && hours !== 12) {
      hours += 12
    } else if (time.format === 'AM' && hours === 12) {
      hours = 0
    }

    fullDate.setHours(hours, minutes, 0, 0)
  }

  return fullDate
}

export function formatConnectedDate(connectedData: Date): string {
  const date = new Date(connectedData)
  const now = new Date()

  const isToday = date.toDateString() === now.toDateString()

  const yesterday = new Date()
  yesterday.setDate(now.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }
  const time = date.toLocaleTimeString('en-US', timeOptions)

  if (isToday) {
    return ` today, ${time}`
  }
  if (isYesterday) {
    return ` yesterday`
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }
  const formattedDate = date.toLocaleDateString('en-US', dateOptions)
  return ` ${formattedDate}`
}

export const formatDateWithTime = (date: Date | string) => {
  const formatedData = new Date(date)
  return formatedData
    .toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    .replace(',', '')
}

export const convertTo12HourFormat = (time: string | undefined) => {
  if (!time) return { hour: '00', minute: '00', second: '00', period: 'AM' }
  const [hourStr, minute, second = '00'] = time.split(':') // Default to "00" if seconds are missing
  let hour = parseInt(hourStr, 10)
  const period = hour >= 12 ? 'PM' : 'AM'
  if (hour === 0) hour = 12
  else if (hour > 12) hour -= 12
  return {
    hour: hour.toString().padStart(2, '0'),
    minute,
    second,
    period,
  }
}

export const convertTo24HourFormat = (
  hour: string,
  minute: string,
  second: string,
  period: string
) => {
  let hourInt = parseInt(hour, 10)
  if (period === 'PM' && hourInt < 12) hourInt += 12
  if (period === 'AM' && hourInt === 12) hourInt = 0
  return `${hourInt.toString().padStart(2, '0')}:${minute}:${second}`
}

export const getRideTime = (rideTime: string) => {
  const time = convertTo12HourFormat(rideTime)
  return `${time.hour}:${time.minute}:${time.second} ${time.period}`
}
